// Filtret på statistiksidan. Gör bara något på formulär märkta med data-breaks-statistics-filter.
// Byte i en lista hämtar sidan i bakgrunden och byter bara ut delarna märkta med
// data-breaks-statistics-region, så att sidan inte laddas om.
//
// Tillgänglighet: fokus ligger kvar i listan, regionerna får aria-busy medan de laddas och
// resultatet läses upp kort via ett role="status"-element i stället för hela innehållet.
// Byten väntar en kort stund, så att piltangenter i en stängd lista inte ger en uppdatering per steg.

import { initRankingLists } from "./rankingList";

const REGION_SELECTOR = "[data-breaks-statistics-region]";
const STATUS_SELECTOR = "[data-breaks-statistics-status]";
const ANNOUNCEMENT_SELECTOR = "[data-breaks-statistics-announcement]";
const DEBOUNCE_MS = 400;

function buildUrl(form: HTMLFormElement): URL {
  const url = new URL(form.action || window.location.href, window.location.href);
  const params = new URLSearchParams();

  new FormData(form).forEach((value, key) => {
    if (typeof value === "string") {
      params.append(key, value);
    }
  });

  url.search = params.toString();
  return url;
}

function setBusy(busy: boolean): void {
  document.querySelectorAll<HTMLElement>(REGION_SELECTOR).forEach((region) => {
    if (busy) {
      region.setAttribute("aria-busy", "true");
    } else {
      region.removeAttribute("aria-busy");
    }
  });
}

function announce(text: string): void {
  const status = document.querySelector<HTMLElement>(STATUS_SELECTOR);

  if (status) {
    status.textContent = text;
  }
}

function swapRegions(doc: Document): void {
  document.querySelectorAll<HTMLElement>(REGION_SELECTOR).forEach((current) => {
    const next = current.id ? doc.getElementById(current.id) : null;

    if (!next) {
      return;
    }

    current.innerHTML = next.innerHTML;
    // Topplistorna är nya element och behöver sin klickhantering igen.
    initRankingLists(current);
  });
}

function initFilter(form: HTMLFormElement): void {
  let controller: AbortController | null = null;
  let timer: number | undefined;

  const update = async (): Promise<void> => {
    const url = buildUrl(form);

    controller?.abort();
    controller = new AbortController();
    setBusy(true);
    announce("Statistiken uppdateras…");

    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      swapRegions(doc);
      history.replaceState(null, "", url);
      setBusy(false);
      announce(
        doc.querySelector(ANNOUNCEMENT_SELECTOR)?.textContent?.trim() ?? "Statistiken är uppdaterad.",
      );
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        // Faller tillbaka på vanlig sidladdning om hämtningen misslyckas.
        window.location.href = url.toString();
      }
    }
  };

  const scheduleUpdate = (): void => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void update(), DEBOUNCE_MS);
  };

  form.querySelectorAll<HTMLSelectElement>("select").forEach((select) => {
    select.addEventListener("change", scheduleUpdate);
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    window.clearTimeout(timer);
    void update();
  });
}

export function initStatisticsFilters(): void {
  document
    .querySelectorAll<HTMLFormElement>("form[data-breaks-statistics-filter]")
    .forEach(initFilter);
}
